import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectMealSchedule,
  setDayPreference,
  resetMealSchedule,
  DayPreference,
  MealDietType,
  DEFAULT_DAY_PREFERENCE,
  DEFAULT_MEAL_SCHEDULE,
} from '../../redux/userPreferencesSlice';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaRadius, PaprikaTypography, PaprikaShadows } from '../../styles/theme';

const DAY_NAME_KEYS = ['thisWeek.dayNames.sunday', 'thisWeek.dayNames.monday', 'thisWeek.dayNames.tuesday', 'thisWeek.dayNames.wednesday', 'thisWeek.dayNames.thursday', 'thisWeek.dayNames.friday', 'thisWeek.dayNames.saturday'];

const MEAL_TYPE_OPTIONS: { value: MealDietType; labelKey: string }[] = [
  { value: 'any', labelKey: 'settings.mealDietTypes.any' },
  { value: 'meat', labelKey: 'settings.mealDietTypes.meat' },
  { value: 'vegetarian', labelKey: 'settings.mealDietTypes.vegetarian' },
  { value: 'fish', labelKey: 'settings.mealDietTypes.fish' },
  { value: 'pasta', labelKey: 'settings.mealDietTypes.pasta' },
];

const TIME_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'NO_LIMIT' },
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
];

const MEALTIME_OPTIONS: { value: string; label12h: string }[] = [
  { value: '17:00', label12h: '5:00 PM' },
  { value: '17:30', label12h: '5:30 PM' },
  { value: '18:00', label12h: '6:00 PM' },
  { value: '18:30', label12h: '6:30 PM' },
  { value: '19:00', label12h: '7:00 PM' },
  { value: '19:30', label12h: '7:30 PM' },
  { value: '20:00', label12h: '8:00 PM' },
];

interface DayCardProps {
  dayOfWeek: number;
  preference: DayPreference;
  onUpdate: (pref: DayPreference) => void;
}

const DayCard: React.FC<DayCardProps> = ({ dayOfWeek, preference, onUpdate }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isFrench = i18n.language === 'fr';
  const getMealTimeLabel = (opt: { value: string; label12h: string }) =>
    isFrench ? opt.value : opt.label12h;

  return (
    <TouchableOpacity
      style={[styles.dayCard, expanded && styles.dayCardExpanded]}
      onPress={() => setExpanded(!expanded)}
      activeOpacity={0.7}
    >
      <View style={styles.dayCardHeader}>
        <Text style={styles.dayName}>{t(DAY_NAME_KEYS[dayOfWeek])}</Text>
        <View style={styles.dayCardSummary}>
          <View style={[styles.mealTypeBadge, getMealTypeColor(preference.mealType)]}>
            <Text style={styles.mealTypeBadgeText}>
              {t(MEAL_TYPE_OPTIONS.find(o => o.value === preference.mealType)?.labelKey || 'settings.mealDietTypes.any')}
            </Text>
          </View>
          {!!preference.maxCookTimeMinutes && (
            <Text style={styles.timeLabel}>{preference.maxCookTimeMinutes}min</Text>
          )}
          {!!preference.defaultMealTime && preference.defaultMealTime !== '18:00' && (
            <Text style={styles.timeLabel}>{getMealTimeLabel(MEALTIME_OPTIONS.find(o => o.value === preference.defaultMealTime) || { value: preference.defaultMealTime, label12h: preference.defaultMealTime })}</Text>
          )}
          {preference.kidsPresent && (
            <Text style={styles.kidsIndicator}>{t('settings.kidsPresent')}</Text>
          )}
        </View>
      </View>

      {expanded && (
        <View style={styles.dayCardBody}>
          {/* Dinner Time Picker */}
          <Text style={styles.optionLabel}>{t('settings.dinnerTime')}</Text>
          <View style={styles.chipRow}>
            {MEALTIME_OPTIONS.map(option => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.chip,
                  (preference.defaultMealTime || '18:00') === option.value && styles.chipActive,
                ]}
                onPress={() => onUpdate({ ...preference, defaultMealTime: option.value })}
              >
                <Text style={[
                  styles.chipText,
                  (preference.defaultMealTime || '18:00') === option.value && styles.chipTextActive,
                ]}>
                  {getMealTimeLabel(option)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Meal Type Picker */}
          <Text style={styles.optionLabel}>{t('settings.mealType')}</Text>
          <View style={styles.chipRow}>
            {MEAL_TYPE_OPTIONS.map(option => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.chip,
                  preference.mealType === option.value && styles.chipActive,
                ]}
                onPress={() => onUpdate({ ...preference, mealType: option.value })}
              >
                <Text style={[
                  styles.chipText,
                  preference.mealType === option.value && styles.chipTextActive,
                ]}>
                  {t(option.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Time Limit Picker */}
          <Text style={styles.optionLabel}>{t('settings.maxCookTime')}</Text>
          <View style={styles.chipRow}>
            {TIME_OPTIONS.map(option => (
              <TouchableOpacity
                key={option.value ?? 'none'}
                style={[
                  styles.chip,
                  preference.maxCookTimeMinutes === option.value && styles.chipActive,
                ]}
                onPress={() => onUpdate({ ...preference, maxCookTimeMinutes: option.value })}
              >
                <Text style={[
                  styles.chipText,
                  preference.maxCookTimeMinutes === option.value && styles.chipTextActive,
                ]}>
                  {option.label === 'NO_LIMIT' ? t('settings.noLimit') : option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Toggles */}
          <View style={styles.toggleRow}>
            <TouchableOpacity
              style={[styles.toggleButton, preference.allowPrepAhead && styles.toggleActive]}
              onPress={() => onUpdate({ ...preference, allowPrepAhead: !preference.allowPrepAhead })}
            >
              <Text style={[styles.toggleText, preference.allowPrepAhead && styles.toggleTextActive]}>
                {t('settings.prepAheadOk')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.toggleButton, preference.kidsPresent && styles.toggleActive]}
              onPress={() => onUpdate({ ...preference, kidsPresent: !preference.kidsPresent })}
            >
              <Text style={[styles.toggleText, preference.kidsPresent && styles.toggleTextActive]}>
                {t('settings.kidsPresent')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
};

function getMealTypeColor(mealType: MealDietType): object {
  switch (mealType) {
    case 'vegetarian': return { backgroundColor: '#E8F5E9' };
    case 'fish': return { backgroundColor: '#E3F2FD' };
    case 'pasta': return { backgroundColor: '#FFF3E0' };
    case 'meat': return { backgroundColor: '#FFEBEE' };
    default: return { backgroundColor: Paprika.surfaceLight };
  }
}

const MealScheduleSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const rawSchedule = useSelector(selectMealSchedule);
  // Handle persisted state from before mealSchedule was added
  const schedule = rawSchedule || DEFAULT_MEAL_SCHEDULE;

  const handleDayUpdate = (dayOfWeek: number, preference: DayPreference) => {
    dispatch(setDayPreference({ dayOfWeek, preference }));
  };

  const handleReset = () => {
    dispatch(resetMealSchedule());
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.sectionTitle}>{t('settings.mealSchedule')}</Text>
        <TouchableOpacity onPress={handleReset} style={styles.resetButton}>
          <Text style={styles.resetText}>{t('settings.reset')}</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.sectionDescription}>
        {t('settings.mealScheduleDescription')}
      </Text>

      {/* Day cards - Monday to Sunday order */}
      {[1, 2, 3, 4, 5, 6, 0].map(dayOfWeek => (
        <DayCard
          key={dayOfWeek}
          dayOfWeek={dayOfWeek}
          preference={schedule.days[dayOfWeek] || DEFAULT_DAY_PREFERENCE}
          onUpdate={(pref) => handleDayUpdate(dayOfWeek, pref)}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: PaprikaSpacing.md,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: PaprikaSpacing.xs,
  },
  sectionTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  sectionDescription: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  resetButton: {
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.xs,
  },
  resetText: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
  },
  dayCard: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    ...PaprikaShadows.card,
  },
  dayCardExpanded: {
    borderColor: Paprika.accent,
  },
  dayCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dayName: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  dayCardSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  mealTypeBadge: {
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 2,
    borderRadius: PaprikaRadius.sm,
  },
  mealTypeBadgeText: {
    ...PaprikaTypography.caption,
    color: Paprika.textPrimary,
  },
  timeLabel: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  kidsIndicator: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
  },
  dayCardBody: {
    marginTop: PaprikaSpacing.md,
    paddingTop: PaprikaSpacing.md,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
  },
  optionLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.xs,
    marginTop: PaprikaSpacing.sm,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.xs,
  },
  chip: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.xs,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLight,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
  },
  chipActive: {
    backgroundColor: Paprika.accentLight,
    borderColor: Paprika.accent,
  },
  chipText: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  chipTextActive: {
    color: Paprika.accent,
  },
  toggleRow: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.md,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: PaprikaSpacing.sm,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.surfaceLight,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    alignItems: 'center',
  },
  toggleActive: {
    backgroundColor: Paprika.accentLight,
    borderColor: Paprika.accent,
  },
  toggleText: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  toggleTextActive: {
    color: Paprika.accent,
  },
});

export default MealScheduleSettings;
